import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { getCURMetrics, executeCURQuery } from '../../services/finopsApi';

const FinOpsOrganizationView = ({ dashboardData, cloudAccounts, onAccountSelect, selectedMonth }) => {
  const [aggregates, setAggregates] = useState([]);
  const [accountCosts, setAccountCosts] = useState({});
  const [loadingAccountCosts, setLoadingAccountCosts] = useState(true);
  const [discountBreakdown, setDiscountBreakdown] = useState(null);
  const [totalTax, setTotalTax] = useState(0);

  const isCurrentMonth = selectedMonth === new Date().toISOString().substring(0, 7);

  useEffect(() => {
    loadAggregates();
    
    // Initialize Bootstrap tooltips
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => {
      if (window.bootstrap && window.bootstrap.Tooltip) {
        return new window.bootstrap.Tooltip(tooltipTriggerEl);
      }
      return null;
    });
    
    return () => {
      tooltipList.forEach(tooltip => tooltip?.dispose());
    };
  }, [selectedMonth]);

  const loadAggregates = async () => {
    try {
      const tenantID = localStorage.getItem('tenantID');
      if (!tenantID) return;
      
      const response = await fetch(`https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/finops/dashboard?tenantID=${tenantID}`);
      if (!response.ok) return;
      
      const data = await response.json();
      if (data.trend && data.trend.months) {
        setAggregates(data.trend.months.slice(-3)); // Last 3 months with services
      }
      
      // Get selected month account costs
      setLoadingAccountCosts(true);
      const queryMonth = selectedMonth || new Date().toISOString().substring(0, 7);
      const accountsResponse = await fetch(`https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/finops/aggregates?tenantID=${tenantID}&month=${queryMonth}`);
      if (accountsResponse.ok) {
        const accountsData = await accountsResponse.json();
        const costsMap = {};
        accountsData.accounts?.forEach(acc => {
          costsMap[acc.accountId] = {
            costData: { mtd_total_cost: parseFloat(acc.totalCost || 0) },
            status: 'success'
          };
        });
        setAccountCosts(costsMap);
        
        // Store discount breakdown if available
        if (accountsData.totalDiscounts && accountsData.totalDiscounts.total < 0) {
          setDiscountBreakdown(accountsData.totalDiscounts);
        }
        if (accountsData.totalTax && accountsData.totalTax > 0) {
          setTotalTax(accountsData.totalTax);
        }
      }
    } catch (error) {
      console.error('Error loading aggregates:', error);
    } finally {
      setLoadingAccountCosts(false);
    }
  };

  // Calculate monthly totals and trend from aggregates
  const monthlyData = aggregates.map(m => {
    const [year, month] = m.month.split('-');
    return {
      month: `${month}-${year.substring(2)}`, // "2025-12" -> "12-25"
      total: m.total
    };
  });
  
  const currentMonthTotal = monthlyData.length > 0 ? monthlyData[monthlyData.length - 1].total : 0;
  const previousMonthTotal = monthlyData.length > 1 ? monthlyData[monthlyData.length - 2].total : 0;
  const trendPercentage = previousMonthTotal > 0 ? ((currentMonthTotal - previousMonthTotal) / previousMonthTotal * 100) : null;
  const trendChartOptions = {
    chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 } },
    xaxis: { 
      categories: dashboardData?.cost?.trend?.map(t => t.date) || [],
      labels: { style: { colors: '#6c757d', fontSize: '11px' } }
    },
    yaxis: { 
      labels: { 
        formatter: (val) => `$${val?.toFixed(0)}`,
        style: { colors: '#6c757d', fontSize: '11px' }
      }
    },
    colors: ['#6D5BFF'],
    grid: { borderColor: '#e9ecef' },
    tooltip: { y: { formatter: (val) => `$${val?.toFixed(2)}` } }
  };

  const trendChartSeries = [{
    name: 'Daily Cost',
    data: dashboardData?.cost?.trend?.map(t => t.cost) || []
  }];

  const budget = dashboardData?.optimization?.budgets?.details?.[0];
  const budgetUsage = budget ? (parseFloat(budget.actual) / parseFloat(budget.limit) * 100) : 0;

  // Dynamically build waste categories
  const wasteCategories = [];
  if (dashboardData?.waste) {
    Object.keys(dashboardData.waste).forEach(key => {
      if (key !== 'summary' && dashboardData.waste[key]?.count) {
        const count = parseInt(dashboardData.waste[key].count || 0);
        if (count > 0) {
          wasteCategories.push({
            key,
            label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
            count,
            color: getWasteColor(key)
          });
        }
      }
    });
  }

  function getWasteColor(key) {
    const colors = {
      idleInstances: '#dc3545',
      stoppedInstances: '#ffc107',
      unattachedVolumes: '#17a2b8',
      unusedEIPs: '#6c757d',
      oldSnapshots: '#6c757d'
    };
    return colors[key] || '#6c757d';
  }

  return (
    <>
      <div className="row g-4">
        <div className="col-md-12">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6 className="text-muted mb-2">{isCurrentMonth ? 'Current Month (MTD)' : selectedMonth}</h6>
                  <h3 className="mb-1" style={{ fontWeight: '700', color: '#342ead' }}>
                    ${dashboardData?.cost?.total?.toFixed(2) || '0.00'}
                  </h3>
                  <p className="text-muted small mb-0">
                    {previousMonthTotal > 0 && (
                      <span>Previous month: ${previousMonthTotal.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                    )}
                  </p>
                </div>
                <div className="col-md-6 text-end">
                  {discountBreakdown && discountBreakdown.total < 0 && (
                    <>
                      <h6 className="text-muted mb-2">Total Savings</h6>
                      <h3 className="mb-1 text-success" style={{ fontWeight: '700' }}>
                        ${discountBreakdown.total.toFixed(2)}
                      </h3>
                      <p className="text-muted small mb-0">
                        <small>
                          {Math.abs(discountBreakdown.edp) > 0.01 && `EDP: $${Math.abs(discountBreakdown.edp).toFixed(2)}`}
                          {Math.abs(discountBreakdown.edp) > 0.01 && Math.abs(discountBreakdown.savingsPlan) > 0.01 && ' | '}
                          {Math.abs(discountBreakdown.savingsPlan) > 0.01 && `SP: $${Math.abs(discountBreakdown.savingsPlan).toFixed(2)}`}
                        </small>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Accounts Table */}
        <div className="col-md-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h5 className="card-title mb-3">Accounts ({cloudAccounts.length})</h5>
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Account Name</th>
                      <th>Account ID</th>
                      <th className="text-end">MTD Cost</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cloudAccounts.map(account => {
                      const accountData = accountCosts[account.accountId];
                      const mtdCost = accountData?.costData?.mtd_total_cost || 0;
                      const isLoading = loadingAccountCosts && !accountData;
                      
                      return (
                        <tr 
                          key={account.accountId} 
                          style={{ cursor: 'pointer' }}
                          onClick={() => onAccountSelect(account.accountId)}
                        >
                          <td>
                            <strong>{account.alias || account.accountName || account.accountId}</strong>
                          </td>
                          <td>
                            <span className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                              {account.accountId}
                            </span>
                          </td>
                          <td className="text-end">
                            {isLoading ? (
                              <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            ) : (
                              <strong style={{ color: '#342ead' }}>
                                ${mtdCost.toLocaleString(undefined, {maximumFractionDigits: 2})}
                              </strong>
                            )}
                          </td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-primary">
                              View Details →
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan="2"><strong>Total</strong></td>
                      <td className="text-end">
                        <strong style={{ color: '#342ead', fontSize: '1.1rem' }}>
                          ${dashboardData?.cost?.total?.toLocaleString(undefined, {maximumFractionDigits: 2}) || '0.00'}
                        </strong>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>

        {isCurrentMonth && wasteCategories.length > 0 && (
          <div className="col-md-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3">Waste Breakdown (All Accounts)</h5>
                <div className="row g-3">
                  {wasteCategories.map(cat => (
                    <div key={cat.key} className="col-md-2">
                      <div className="text-center p-3 border rounded">
                        <h4 className="mb-1" style={{ color: cat.color }}>{cat.count}</h4>
                        <small className="text-muted">{cat.label}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {monthlyData.length > 0 && (
          <div className="col-md-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3">Cost Trend (Last 3 Months)</h5>
                <Chart 
                  options={{
                    chart: { type: 'line', toolbar: { show: false } },
                    stroke: { curve: 'smooth', width: 3 },
                    xaxis: { categories: monthlyData.map(m => m.month) },
                    yaxis: { labels: { formatter: (val) => `$${(val/1000).toFixed(0)}K` } },
                    colors: ['#342ead'],
                    markers: { size: 6 },
                    tooltip: { y: { formatter: (val) => `$${val.toLocaleString()}` } },
                    dataLabels: { enabled: true, formatter: (val) => `$${(val/1000).toFixed(1)}K` }
                  }}
                  series={[{ name: 'Total Cost', data: monthlyData.map(m => m.total) }]}
                  type="line"
                  height={300}
                />
              </div>
            </div>
          </div>
        )}

        {aggregates.length > 0 && aggregates[0].services && (
          <div className="col-md-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3">Top Services Trend (Last 3 Months)</h5>
                <Chart 
                  options={{
                    chart: { 
                      type: 'bar', 
                      stacked: true,  // Stack bars like AWS Cost Explorer
                      toolbar: { show: false },
                      animations: { enabled: true }
                    },
                    plotOptions: { 
                      bar: { 
                        horizontal: false, 
                        columnWidth: '60%',
                        dataLabels: { position: 'center' }  // Center labels in bars
                      } 
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: (val) => val > 100 ? `$${(val/1000).toFixed(1)}K` : '',  // Only show labels for significant values
                      style: { fontSize: '11px', colors: ['#fff'], fontWeight: 'bold' },
                      offsetY: 0
                    },
                    xaxis: { 
                      categories: aggregates.map(m => {
                        const [year, month] = m.month.split('-');
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        return `${monthNames[parseInt(month)-1]} ${year}`;
                      }),
                      labels: { style: { fontSize: '12px', fontWeight: 600 } }
                    },
                    yaxis: { 
                      labels: { 
                        formatter: (val) => `$${(val/1000).toFixed(1)}K`,
                        style: { fontSize: '12px' }
                      },
                      title: { text: 'Cost (USD)', style: { fontSize: '13px', fontWeight: 600 } }
                    },
                    legend: { 
                      position: 'bottom',
                      horizontalAlign: 'center',
                      fontSize: '12px',
                      markers: { width: 12, height: 12, radius: 2 },
                      itemMargin: { horizontal: 10, vertical: 5 },
                      formatter: (seriesName, opts) => {
                        const total = opts.w.globals.series[opts.seriesIndex].reduce((a, b) => a + b, 0);
                        return `${seriesName}: $${(total/1000).toFixed(1)}K`;
                      }
                    },
                    tooltip: { 
                      shared: true,
                      intersect: false,
                      y: { 
                        formatter: (val) => `$${val.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` 
                      },
                      x: { show: true }
                    },
                    colors: ['#342ead', '#6D5BFF', '#9D8DFF', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#546E7A']
                  }}
                  series={(() => {
                    const allServices = new Set();
                    aggregates.forEach(m => m.services?.slice(0, 8).forEach(s => allServices.add(s.service)));
                    return Array.from(allServices).slice(0, 8).map(serviceName => ({
                      name: serviceName.length > 40 ? serviceName.substring(0, 40) + '...' : serviceName,
                      data: aggregates.map(m => {
                        const svc = m.services?.find(s => s.service === serviceName);
                        return svc ? parseFloat(svc.cost) : 0;
                      })
                    }));
                  })()}
                  type="bar"
                  height={400}
                />
              </div>
            </div>
          </div>
        )}

        {isCurrentMonth && (
          <div className="col-md-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3">Daily Cost Trend (Current Month)</h5>
                <Chart options={trendChartOptions} series={trendChartSeries} type="area" height={280} />
              </div>
            </div>
          </div>
        )}



        <div className="col-md-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h5 className="card-title mb-3">Service Cost Breakdown</h5>
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Service</th>
                      <th className="text-end" style={{ width: '120px', paddingRight: '15px' }}>Cost</th>
                      <th className="text-end" style={{ width: '80px', paddingRight: '15px' }}>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.cost?.byService?.slice(0, 10).map((service, idx) => {
                      const servicesSum = dashboardData?.cost?.byService?.reduce((sum, s) => sum + parseFloat(s.cost), 0) || 1;
                      const percentage = (parseFloat(service.cost) / servicesSum * 100).toFixed(1);
                      return (
                        <tr key={idx}>
                          <td>{service.service}</td>
                          <td className="text-end" style={{ fontWeight: '500' }}>
                            ${parseFloat(service.cost).toFixed(2)}
                          </td>
                          <td className="text-end">
                            <span className="badge" style={{ 
                              backgroundColor: percentage > 20 ? '#dc3545' : percentage > 10 ? '#ffc107' : '#e9ecef',
                              color: percentage > 10 ? '#fff' : '#6c757d'
                            }}>
                              {percentage}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td><strong>Gross Cost</strong></td>
                      <td className="text-end" colSpan="2">
                        <strong>${(() => {
                          const servicesSum = dashboardData?.cost?.byService?.reduce((sum, s) => sum + parseFloat(s.cost), 0) || 0;
                          return servicesSum.toFixed(2);
                        })()}</strong>
                      </td>
                    </tr>
                    {(() => {
                      const servicesSum = dashboardData?.cost?.byService?.reduce((sum, s) => sum + parseFloat(s.cost), 0) || 0;
                      const discount = servicesSum - (dashboardData?.cost?.total || 0);
                      return discount > 1 ? (
                        <tr>
                          <td><strong>Total Savings</strong></td>
                          <td className="text-end text-success" colSpan="2">
                            <strong>-${discount.toFixed(2)}</strong>
                          </td>
                        </tr>
                      ) : null;
                    })()}
                    <tr style={{ borderTop: '2px solid #dee2e6' }}>
                      <td><strong>Net Cost</strong></td>
                      <td className="text-end" colSpan="2">
                        <strong style={{ color: '#342ead', fontSize: '1.1rem' }}>
                          ${dashboardData?.cost?.total?.toFixed(2) || '0.00'}
                        </strong>
                      </td>
                    </tr>
                    {totalTax > 0 && (
                      <tr>
                        <td><em className="text-muted">  (includes Tax)</em></td>
                        <td className="text-end text-muted" colSpan="2">
                          <em>${totalTax.toFixed(2)} ({((totalTax / (dashboardData?.cost?.total || 1)) * 100).toFixed(1)}%)</em>
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FinOpsOrganizationView;
